import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { money, dateFmt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ method: "Bank Transfer" });

  const { data: inv, isLoading } = useQuery({ queryKey: ["invoice", id], queryFn: () => get(`/finance/invoices/${id}`) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const record = useMutation({
    mutationFn: (body) => post(`/finance/invoices/${id}/payments`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoice", id] }); setOpen(false); toast.success("Payment recorded"); },
  });

  if (isLoading || !inv) return <Skeleton className="h-96" />;

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        <button onClick={() => navigate("/finance/invoices")} className="hover:underline">Invoices</button> / {inv.number}
      </div>
      <div className="mb-6 flex items-center gap-3">
        <h1 className="font-display text-2xl font-bold tracking-tight">{inv.number}</h1>
        <StatusBadge status={inv.status} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="mb-3 font-display text-sm font-semibold">Line Items</h3>
            <table className="w-full text-sm">
              <tbody>
                {inv.line_items.map((li) => (
                  <tr key={li.id} className="border-b border-border/60"><td className="py-2">{li.description}</td><td className="py-2 text-right font-medium">{money(li.qty * li.rate)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 flex justify-between border-t border-border pt-3 font-display text-lg font-bold"><span>Total</span><span>{money(inv.amount)}</span></div>
            <div className="mt-1 flex justify-between text-sm text-muted-foreground"><span>Balance Due</span><span className="font-semibold text-foreground">{money(inv.balance)}</span></div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-sm font-semibold">Payments</h3>
              <Button size="sm" data-testid="record-payment" disabled={inv.balance <= 0} onClick={() => { setForm({ method: "Bank Transfer", amount: inv.balance }); setOpen(true); }}>Record Payment</Button>
            </div>
            {inv.payments.length ? inv.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-border/60 py-2 text-sm">
                <div><div className="font-medium">{money(p.amount)}</div><div className="text-xs text-muted-foreground">{p.method} · {p.reference}</div></div>
                <span className="text-xs text-muted-foreground">{dateFmt(p.date)}</span>
              </div>
            )) : <p className="text-sm text-muted-foreground">No payments yet.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Client</dt><dd className="font-medium">{inv.client_name}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Project</dt><dd>{inv.project || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Due Date</dt><dd>{dateFmt(inv.due_date)}</dd></div>
          </dl>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="payment-modal">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount</Label><Input type="number" data-testid="pay-amount" value={form.amount || ""} onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))} className="mt-1" /></div>
            <div><Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["Bank Transfer", "Cheque", "Card", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reference</Label><Input data-testid="pay-ref" onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-payment" onClick={() => record.mutate({ ...form, date: new Date().toISOString(), by: users[0]?.id })}>Save Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
